import {
  IPracticeSetupWizard,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  initMoment,
  toTimestamp,
} from '@principle-theorem/shared';
import { duration, Moment } from 'moment-timezone';

/**
 * Tracks staffer engagement with a practice setup wizard by recording
 * the last interaction timestamp per staffer on the wizard document.
 *
 * Writes are throttled to at most once per 5 minutes per wizard–staffer
 * pair using an in-memory cache, preventing excessive Firestore writes
 * when a staffer repeatedly visits wizard-related pages.
 */
export class SetupEngagementTracker {
  private static readonly _lastSuccessfulWriteByKey = new Map<string, Moment>();

  /**
   * Record a staffer's engagement with a setup wizard.
   *
   * Updates `lastEngagementByStaffer.<stafferId>` on the wizard document.
   * Silently skips the write if the same wizard–staffer pair was recorded
   * within the last 5 minutes, or if the Firestore update fails.
   */
  static async recordEngagement(
    wizardRef: DocumentReference<IPracticeSetupWizard>,
    stafferRef: DocumentReference<IStaffer>
  ): Promise<void> {
    const engagementKey = `${wizardRef.path}::${stafferRef.path}`;
    const lastSentAt = this._lastSuccessfulWriteByKey.get(engagementKey);

    if (
      lastSentAt !== undefined &&
      initMoment().isBefore(lastSentAt.clone().add(duration(5, 'minutes')))
    ) {
      return;
    }

    try {
      await Firestore.updateDoc(wizardRef, {
        [`lastEngagementByStaffer.${stafferRef.id}`]: toTimestamp(),
      });

      this._lastSuccessfulWriteByKey.set(engagementKey, initMoment());
    } catch {
      return;
    }
  }
}
